import * as React from 'react'
import * as v from 'valibot'
import { useLingui } from '@lingui/react/macro'

/**
 * The translated validation messages every form renders, installed on valibot's global message table.
 * No per-locale pack to fetch — every message here comes from Lingui, already loaded.
 *
 * `setSpecificMessage` is keyed on the ACTION (`v.minLength`, `v.email`), which is why the "is this
 * really just Required?" special-cases below read as separate registrations rather than one switch.
 * Global and process-wide: fine in a browser, and nothing imports this outside the client form layer.
 */
export function useFormI18n() {
  const { t } = useLingui()

  React.useEffect(() => {
    // A schema-kind issue is a wrong or missing type. In a form that almost always means the field was
    // left empty (string, number, boolean, enum, date…), so it reads as "Required" rather than
    // valibot's "Invalid type: Expected string but received undefined".
    v.setSchemaMessage(() => t`Required`)

    // Each message binds the interpolated value to a local first: Lingui's extractor only accepts a
    // bare `${variable}`, not `${issue.requirement}` or `${String(...)}`.
    v.setSpecificMessage(v.minLength, (issue) => {
      const min = String(issue.requirement)
      return issue.requirement === 1 ? t`Required` : t`Minimum ${min} characters`
    })
    v.setSpecificMessage(v.maxLength, (issue) => {
      const max = String(issue.requirement)
      return t`Maximum ${max} characters`
    })
    v.setSpecificMessage(v.minValue, (issue) => {
      const min = String(issue.requirement)
      return t`Minimum ${min}`
    })
    v.setSpecificMessage(v.maxValue, (issue) => {
      const max = String(issue.requirement)
      return t`Maximum ${max}`
    })
    v.setSpecificMessage(v.minSize, (issue) => {
      const min = String(issue.requirement)
      return issue.requirement === 1 ? t`Required` : t`Minimum ${min}`
    })
    v.setSpecificMessage(v.email, () => t`Invalid email`)
    v.setSpecificMessage(v.url, () => t`Invalid URL`)
    v.setSpecificMessage(v.isoDate, () => t`Invalid date`)
    v.setSpecificMessage(v.isoDateTime, () => t`Invalid date`)
    // `v.literal(true)` is a consent checkbox — only the terms-of-use fields use it. Any other literal
    // falls through to the schema message.
    v.setSpecificMessage(v.literal, (issue) =>
      issue.expected === 'true' ? t`You must accept the terms of use` : t`Required`,
    )
  }, [t])
}
